import { OutlineChevronDownIcon, OutlineChevronUpIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { useState, type ReactNode } from 'react';

interface ISectionCardProps {
  title: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  testId?: string;
  /**
   * When true, the card title row gets a chevron toggle and clicking the title row
   * collapses or expands the section content (and divider). Defaults to false.
   */
  collapsible?: boolean;
  /** Initial collapsed state when `collapsible` is true. Defaults to false (expanded). */
  defaultCollapsed?: boolean;
}

export const SectionCard = ({
  title,
  headerRight,
  children,
  testId,
  collapsible = false,
  defaultCollapsed = false,
}: ISectionCardProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const isOpen = !collapsible || !collapsed;

  return (
    <Column
      data-testid={testId}
      data-cy={testId}
      style={{
        backgroundColor: colors.shades[0],
        border: `1.5px solid ${colors.neutral[100]}`,
        borderRadius: 8,
        padding: '28px 28px 36px 28px',
      }}
    >
      <Row
        justify="between"
        align="center"
        onClick={collapsible ? () => setCollapsed((v) => !v) : undefined}
        style={collapsible ? { cursor: 'pointer' } : undefined}
        data-testid={collapsible ? `${testId}-header` : undefined}
      >
        {typeof title === 'string' ? (
          <Typography as="span" bold size="heading_3">
            {title}
          </Typography>
        ) : (
          title
        )}
        {(headerRight || collapsible) && (
          <Row align="center" gap={2}>
            {headerRight}
            {collapsible &&
              (isOpen ? (
                <OutlineChevronUpIcon color={colors.secondary[500]} />
              ) : (
                <OutlineChevronDownIcon color={colors.secondary[500]} />
              ))}
          </Row>
        )}
      </Row>

      {isOpen && (
        <>
          <Box
            style={{
              height: '1.5px',
              backgroundColor: colors.neutral[100],
              marginTop: 13,
              marginBottom: 20,
            }}
          />
          {children}
        </>
      )}
    </Column>
  );
};
